import { Blocks, Code, Eye, EyeOff, MessageCircle, Type } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { VariablePicker } from '../../../../components/VariablePicker';
import { SlackWizardConfig } from '../SlackWizard';

interface MessageFormatStepProps {
	nodeId: string;
	config: SlackWizardConfig;
	onChange: (updates: Partial<SlackWizardConfig>) => void;
	errors: string[];
}

const MESSAGE_FORMATS = [
	{
		id: 'text' as const,
		title: 'Plain Text',
		description: 'Simple text message without formatting',
		icon: Type,
		example: 'Hello world! Order {{order.id}} has been processed.',
	},
	{
		id: 'markdown' as const,
		title: 'Markdown',
		description: 'Rich text with *bold*, _italic_, and other Slack markdown',
		icon: Code,
		example: '*Order Update:* Your order `{{order.id}}` has been _successfully_ processed!',
	},
	{
		id: 'blocks' as const,
		title: 'Block Kit',
		description: 'Advanced rich formatting with interactive elements',
		icon: Blocks,
		example: 'Complex layouts with buttons, sections, and rich media',
	},
];

const SAMPLE_BLOCK_KIT = [
	{
		type: 'section',
		text: {
			type: 'mrkdwn',
			text: '*Order Update* 📦\nYour order `{{order.id}}` has been processed successfully!',
		},
	},
	{
		type: 'section',
		fields: [
			{
				type: 'mrkdwn',
				text: '*Customer:*\n{{customer.name}}',
			},
			{
				type: 'mrkdwn',
				text: '*Amount:*\n${{order.total}}',
			},
		],
	},
	{
		type: 'actions',
		elements: [
			{
				type: 'button',
				text: {
					type: 'plain_text',
					text: 'View Order',
				},
				url: '{{order.url}}',
				style: 'primary',
			},
		],
	},
];

export function MessageFormatStep({ nodeId, config, onChange, errors }: MessageFormatStepProps) {
	const [showJsonEditor, setShowJsonEditor] = useState(false);
	const [jsonError, setJsonError] = useState<string | null>(null);
	const textAreaRef = useRef<HTMLTextAreaElement>(null);

	const handleFormatChange = useCallback(
		(format: 'text' | 'markdown' | 'blocks') => {
			const updates: Partial<SlackWizardConfig> = { format };

			// Initialize content based on format
			if (
				format === 'blocks' &&
				(!config.content.blocks || config.content.blocks.length === 0)
			) {
				updates.content = {
					...config.content,
					blocks: SAMPLE_BLOCK_KIT,
				};
			} else if ((format === 'text' || format === 'markdown') && !config.content.text) {
				updates.content = {
					...config.content,
					text: MESSAGE_FORMATS.find(f => f.id === format)?.example || '',
				};
			}

			onChange(updates);
		},
		[config.content, onChange]
	);

	const handleTextChange = useCallback(
		(text: string) => {
			onChange({
				content: {
					...config.content,
					text,
				},
			});
		},
		[config.content, onChange]
	);

	const handleBlocksChange = useCallback(
		(blocksJson: string) => {
			try {
				const blocks = JSON.parse(blocksJson);
				onChange({
					content: {
						...config.content,
						blocks,
					},
				});
				setJsonError(null);
			} catch (error) {
				setJsonError(error instanceof Error ? error.message : 'Invalid JSON');
			}
		},
		[config.content, onChange]
	);

	const handleInsertSampleBlocks = useCallback(() => {
		onChange({
			content: {
				...config.content,
				blocks: SAMPLE_BLOCK_KIT,
			},
		});
		setJsonError(null);
	}, [config.content, onChange]);

	const currentFormat = MESSAGE_FORMATS.find(f => f.id === config.format);

	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
					Message Format & Content
				</h3>
				<p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
					Choose how your message will be formatted and compose your content with dynamic
					variables.
				</p>
			</div>

			{/* Format Selection */}
			<div className="space-y-3">
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
					Message Format
				</label>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
					{MESSAGE_FORMATS.map(format => {
						const Icon = format.icon;
						const isSelected = config.format === format.id;

						return (
							<div
								key={format.id}
								className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${
									isSelected
										? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
										: 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
								}`}
								onClick={() => handleFormatChange(format.id)}
							>
								<div className="flex items-start">
									<Icon
										className={`w-5 h-5 mt-0.5 ${
											isSelected
												? 'text-blue-600 dark:text-blue-400'
												: 'text-gray-400'
										}`}
									/>
									<div className="ml-3 flex-1">
										<h4
											className={`text-sm font-medium ${
												isSelected
													? 'text-blue-900 dark:text-blue-100'
													: 'text-gray-900 dark:text-white'
											}`}
										>
											{format.title}
										</h4>
										<p
											className={`text-xs mt-1 ${
												isSelected
													? 'text-blue-700 dark:text-blue-300'
													: 'text-gray-500 dark:text-gray-400'
											}`}
										>
											{format.description}
										</p>
									</div>
								</div>
								{isSelected && (
									<div className="absolute top-2 right-2">
										<div className="w-2 h-2 bg-blue-600 rounded-full"></div>
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>

			{/* Content Editor */}
			<div className="space-y-3">
				<div className="flex items-center justify-between">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Message Content
					</label>
					{config.format === 'blocks' && (
						<div className="flex items-center space-x-2">
							<button
								type="button"
								onClick={() => setShowJsonEditor(!showJsonEditor)}
								className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600"
							>
								{showJsonEditor ? (
									<EyeOff className="w-3 h-3 mr-1" />
								) : (
									<Eye className="w-3 h-3 mr-1" />
								)}
								{showJsonEditor ? 'Hide JSON' : 'Show JSON'}
							</button>
							<button
								type="button"
								onClick={handleInsertSampleBlocks}
								className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30"
							>
								Insert Sample
							</button>
						</div>
					)}
				</div>

				{/* Text/Markdown Editor */}
				{(config.format === 'text' || config.format === 'markdown') && (
					<div className="space-y-3">
						<div className="space-y-2">
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
								Message Content
							</label>
							<VariablePicker
								nodeId={nodeId}
								value={config.content.text || ''}
								onChange={handleTextChange}
								placeholder={`Enter your ${config.format} message here... Type {{ to insert variables`}
								multiline={true}
								className="min-h-32"
								ref={textAreaRef}
							/>
						</div>
						<div className="flex items-start space-x-2 text-xs text-gray-500 dark:text-gray-400">
							<div className="flex-1">
								<p>
									💡 <strong>Tip:</strong> Type <code>{'{{'}</code> to insert
									dynamic values from upstream nodes, or click the "Insert
									Variable" button.
								</p>
								{config.format === 'markdown' && (
									<p className="mt-1">
										Slack markdown: *bold*, _italic_, `code`, ~strikethrough~,
										&gt;quote
									</p>
								)}
							</div>
						</div>
					</div>
				)}

				{/* Block Kit Editor */}
				{config.format === 'blocks' && (
					<div className="space-y-3">
						{showJsonEditor ? (
							<div className="space-y-2">
								<textarea
									value={JSON.stringify(config.content.blocks || [], null, 2)}
									onChange={e => handleBlocksChange(e.target.value)}
									placeholder="Enter Block Kit JSON..."
									className={`block w-full px-3 py-2 border rounded-md shadow-sm font-mono text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 min-h-64 ${
										jsonError
											? 'border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-500'
											: 'border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500'
									} bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
								/>
								{jsonError && (
									<p className="text-sm text-red-600 dark:text-red-400">
										JSON Error: {jsonError}
									</p>
								)}
							</div>
						) : (
							<div className="border border-gray-300 dark:border-gray-600 rounded-md p-4 bg-gray-50 dark:bg-gray-700">
								<div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
									Block Kit Preview (JSON editor available above)
								</div>
								<pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
									{JSON.stringify(config.content.blocks || [], null, 2)}
								</pre>
							</div>
						)}

						<div className="text-xs text-gray-500 dark:text-gray-400">
							<p>
								Use Block Kit for rich layouts with sections, buttons, and
								interactive elements. Variables like {'{{order.id}}'} work in text
								fields.
							</p>
							<p className="mt-1">
								<a
									href="https://api.slack.com/block-kit"
									target="_blank"
									rel="noopener noreferrer"
									className="text-blue-600 dark:text-blue-400 hover:underline"
								>
									Learn more about Block Kit →
								</a>
							</p>
						</div>
					</div>
				)}
			</div>

			{/* Format Example */}
			{currentFormat && (
				<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md p-4">
					<h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
						{currentFormat.title} Example
					</h4>
					<div className="text-sm text-blue-800 dark:text-blue-200 font-mono bg-white dark:bg-gray-800 rounded p-2 border">
						{config.format === 'blocks' ? (
							<pre className="whitespace-pre-wrap text-xs">
								{JSON.stringify(SAMPLE_BLOCK_KIT, null, 2)}
							</pre>
						) : (
							currentFormat.example
						)}
					</div>
				</div>
			)}

			{/* Error Display */}
			{errors.length > 0 && (
				<div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
					<div className="flex">
						<MessageCircle className="h-5 w-5 text-red-400" />
						<div className="ml-3">
							<h3 className="text-sm font-medium text-red-800 dark:text-red-200">
								Content Issues
							</h3>
							<div className="mt-2 text-sm text-red-700 dark:text-red-300">
								<ul className="list-disc list-inside space-y-1">
									{errors.map((error, index) => (
										<li key={index}>{error}</li>
									))}
								</ul>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
